import { theaterOrganizationRouteNames } from '../../../../routing/routeNames/theaterOrganizerRouterNames'

const {
  theaterOrganizationEvents,
  theaterLiveEvents,
  theaterPastEvents,
  theaterOrganizationAddEvent,
  theaterOrganizationProfileInfo,
  theaterOrganizationFinanceInfo,
  theaterOrganizationSubscribers,
  theaterOrganizationGenres,
  theaterOrganizationTags,
  theaterOrganizationSubscriptionsPlan,
  theaterOrganizationSubscriptionsList,
  theaterOrganizationTheaterSubscriptionsPlan,
  theaterOrganizationTheaterSubscriptionsList,
  theaterOrganizationTheaterSubscribersMessage,
  theaterOrganizationTheaterSubscribersEmails,
  theaterOrganizationTheaterSubscribersEmailDetails,
  theaterOrganizationTheaterSubscriptionsFloorPlan,
  theaterOrganizationTheaterSubscriptionsReports,
  theaterOrganizationTicketType,
  theaterOrganizationMailingList,
  theaterOrganizationPromoCodes,
  theaterOrganizationPromoCodeInfo,
  theaterOrganizationMemberships,
  theaterOrganizationMembersList,
  theaterOrganizationReports,
  theaterOrganizationPayouts,
  theaterOrganizationTicketingDeals,
  theaterOrganizationBundleOffers,
  theaterOrganizationUsers,
} = theaterOrganizationRouteNames

/**
 * Sidebar keys for mapping routes to sidebar links
 * Some are string literals because for those links there isn't any route, they are just dropdowns for their subroutes
 *
 * They are in order as they are in Sidebar
 */
const sidebarKeys = {
  events: theaterOrganizationEvents,
  liveEvents: theaterLiveEvents,
  pastEvents: theaterPastEvents,
  organizationInfo: theaterOrganizationProfileInfo,
  financeInfo: theaterOrganizationFinanceInfo,
  subscribers: theaterOrganizationSubscribers,
  genres: theaterOrganizationGenres,
  tags: theaterOrganizationTags,
  subscriptionsPlan: theaterOrganizationSubscriptionsPlan,
  subscriptionsList: theaterOrganizationSubscriptionsList,
  theaterSubscriptionsPlan: theaterOrganizationTheaterSubscriptionsPlan,
  theaterSubscriptionsList: theaterOrganizationTheaterSubscriptionsList,
  theaterSubscribersEmails: theaterOrganizationTheaterSubscribersEmails,
  theaterSubscriptionsReports: theaterOrganizationTheaterSubscriptionsReports,
  theaterSubscriptionsFloorPlan: theaterOrganizationTheaterSubscriptionsFloorPlan,
  ticketType: theaterOrganizationTicketType,
  mailingList: theaterOrganizationMailingList,
  promoCodes: theaterOrganizationPromoCodes,
  memberships: theaterOrganizationMemberships,
  membersList: theaterOrganizationMembersList,
  reports: theaterOrganizationReports,
  payouts: theaterOrganizationPayouts,
  ticketingDeals: theaterOrganizationTicketingDeals,
  bundleOffers: theaterOrganizationBundleOffers,
  users: theaterOrganizationUsers,
}

/**
 * Sidebar links - objects for sidebar configuration
 *
 * Type:
 * {
 *    label: Name displayed in sidebar
 *    icon: Name of icon, representing the one inside assets/sidebar-icons
 *    key: Key for mapping the link to sidebar, take from sidebarKeys
 *    modulesRequired?: List of modules required for displaying this link
 *    allowedRoles?: List of roles for which this link will be displayed
 *    linkedRoutes: List of all routes for which this link will be highlighted as active and disabled for clicking (including its internal route)
 *    internalRoute: {
 *      name: Name of route this link will lead to, take from routeNames object
 *    }
 *    subroutes? List of route objects, only dropdowns have this property
 * }
 */
export const theaterOrganizationSidebarConfiguration = [
  {
    label: 'My events',
    key: sidebarKeys.events,
    icon: 'events',
    subroutes: [
      {
        label: 'Live events',
        key: sidebarKeys.liveEvents,
        linkedRoutes: [theaterLiveEvents],
        internalRoute: {
          name: theaterLiveEvents,
        },
      },
      {
        label: 'Past events',
        key: sidebarKeys.pastEvents,
        linkedRoutes: [theaterPastEvents],
        internalRoute: {
          name: theaterPastEvents,
        },
      },
    ],
    linkedRoutes: [
      theaterOrganizationEvents,
      theaterOrganizationAddEvent,
      theaterLiveEvents,
      theaterPastEvents,
    ],
    internalRoute: {
      name: theaterLiveEvents,
    },
  },
  {
    label: 'Organization info',
    key: sidebarKeys.organizationInfo,
    icon: 'organization-info',
    linkedRoutes: [theaterOrganizationProfileInfo],
    internalRoute: {
      name: theaterOrganizationProfileInfo,
    },
  },
  {
    label: 'Finance info',
    key: sidebarKeys.financeInfo,
    linkedRoutes: [theaterOrganizationFinanceInfo],
    icon: 'finance-info',
    internalRoute: {
      name: theaterOrganizationFinanceInfo,
    },
  },
  {
    label: 'Payouts',
    key: sidebarKeys.payouts,
    linkedRoutes: [theaterOrganizationPayouts],
    icon: 'reports',
    internalRoute: {
      name: theaterOrganizationPayouts,
    },
  },
  {
    label: 'Mailing list',
    key: sidebarKeys.mailingList,
    icon: 'mailing-list',
    linkedRoutes: [theaterOrganizationMailingList],
    internalRoute: {
      name: theaterOrganizationMailingList,
    },
  },
  {
    label: 'Genres and tags',
    key: sidebarKeys.genres,
    icon: 'genres-and-tags',
    subroutes: [
      {
        label: 'Genres',
        key: sidebarKeys.genres,
        linkedRoutes: [theaterOrganizationGenres],
        internalRoute: {
          name: theaterOrganizationGenres,
        },
      },
      {
        label: 'Tags',
        key: sidebarKeys.tags,
        linkedRoutes: [theaterOrganizationTags],
        internalRoute: {
          name: theaterOrganizationTags,
        },
      },
    ],
    linkedRoutes: [theaterOrganizationGenres, theaterOrganizationTags],
    internalRoute: {
      name: theaterOrganizationGenres,
    },
  },
  {
    label: 'Ticket types',
    key: sidebarKeys.ticketType,
    icon: 'ticket-types',
    linkedRoutes: [theaterOrganizationTicketType],
    internalRoute: {
      name: theaterOrganizationTicketType,
    },
  },
  {
    label: 'Ticket Packages',
    key: 'sidebarKeys.organizationInfo',
    icon: 'bundles',
    subroutes: [
      {
        label: 'Package Plans',
        key: sidebarKeys.subscriptionsPlan,
        linkedRoutes: [theaterOrganizationSubscriptionsPlan],
        internalRoute: {
          name: theaterOrganizationSubscriptionsPlan,
        },
      },
      {
        label: 'Package List',
        key: sidebarKeys.subscriptionsList,
        linkedRoutes: [theaterOrganizationSubscriptionsList],
        internalRoute: {
          name: theaterOrganizationSubscriptionsList,
        },
      },
    ],
    linkedRoutes: [theaterOrganizationSubscriptionsPlan, theaterOrganizationSubscriptionsList],
    internalRoute: {
      name: theaterOrganizationSubscriptionsPlan,
    },
  },
  {
    label: 'Subscriptions',
    key: 'sidebarKeys.theaterSubscriptions',
    icon: 'subscriptions',
    subroutes: [
      {
        label: 'Subscription Plans',
        key: sidebarKeys.theaterSubscriptionsPlan,
        linkedRoutes: [theaterOrganizationTheaterSubscriptionsPlan],
        internalRoute: {
          name: theaterOrganizationTheaterSubscriptionsPlan,
        },
      },
      {
        label: 'Subscribers List',
        key: sidebarKeys.theaterSubscriptionsList,
        linkedRoutes: [theaterOrganizationTheaterSubscriptionsList],
        internalRoute: {
          name: theaterOrganizationTheaterSubscriptionsList,
        },
      },
      {
        label: 'Email to Subscribers',
        key: sidebarKeys.theaterSubscribersEmails,
        linkedRoutes: [
          theaterOrganizationTheaterSubscribersEmails,
          theaterOrganizationTheaterSubscribersEmailDetails,
          theaterOrganizationTheaterSubscribersMessage,
        ],
        internalRoute: {
          name: theaterOrganizationTheaterSubscribersEmails,
        },
      },
      {
        label: 'Floor Plan',
        key: sidebarKeys.theaterSubscriptionsFloorPlan,
        linkedRoutes: [theaterOrganizationTheaterSubscriptionsFloorPlan],
        internalRoute: {
          name: theaterOrganizationTheaterSubscriptionsFloorPlan,
        },
      },
      {
        label: 'Subscription Reports',
        key: sidebarKeys.theaterSubscriptionsReports,
        linkedRoutes: [theaterOrganizationTheaterSubscriptionsReports],
        internalRoute: {
          name: theaterOrganizationTheaterSubscriptionsReports,
        },
      },
    ],
    linkedRoutes: [
      theaterOrganizationTheaterSubscriptionsPlan,
      theaterOrganizationTheaterSubscriptionsList,
      theaterOrganizationTheaterSubscribersEmails,
      theaterOrganizationTheaterSubscribersEmailDetails,
      theaterOrganizationTheaterSubscribersMessage,
      theaterOrganizationTheaterSubscriptionsReports,
    ],
    internalRoute: {
      name: theaterOrganizationTheaterSubscriptionsPlan,
    },
  },
  {
    label: 'Users',
    key: sidebarKeys.users,
    icon: 'people',
    linkedRoutes: [theaterOrganizationUsers],
    allowedOrganizationRoles: ['OWNER', 'ADMIN'],
    internalRoute: {
      name: theaterOrganizationUsers,
    },
  },
  {
    label: 'Ticketing deals',
    key: sidebarKeys.ticketingDeals,
    icon: 'promo-codes',
    linkedRoutes: [
      theaterOrganizationTicketingDeals,
      theaterOrganizationPromoCodeInfo,
      theaterOrganizationBundleOffers,
    ],
    internalRoute: {
      name: theaterOrganizationTicketingDeals,
    },
    subroutes: [
      {
        label: 'Promo codes',
        key: sidebarKeys.promoCodes,
        icon: 'promo-codes',
        linkedRoutes: [theaterOrganizationPromoCodes, theaterOrganizationPromoCodeInfo],
        internalRoute: {
          name: theaterOrganizationPromoCodes,
        },
      },
      {
        label: 'Bundle offers',
        key: sidebarKeys.bundleOffers,
        icon: 'bundles',
        linkedRoutes: [theaterOrganizationBundleOffers],
        internalRoute: {
          name: theaterOrganizationBundleOffers,
        },
      },
    ],
  },
  {
    label: 'Memberships',
    key: 'sidebarKeys.memberships',
    icon: 'memberships',
    subroutes: [
      {
        label: 'Memberships',
        key: sidebarKeys.memberships,
        linkedRoutes: [theaterOrganizationMemberships],
        internalRoute: {
          name: theaterOrganizationMemberships,
        },
      },
      {
        label: 'Members List',
        key: sidebarKeys.membersList,
        linkedRoutes: [theaterOrganizationMembersList],
        internalRoute: {
          name: theaterOrganizationMembersList,
        },
      },
    ],
    linkedRoutes: [theaterOrganizationMemberships, theaterOrganizationMembersList],
    internalRoute: {
      name: theaterOrganizationMembersList,
    },
  },
  {
    label: 'Reports',
    key: sidebarKeys.reports,
    icon: 'reports',
    linkedRoutes: [theaterOrganizationReports],
    internalRoute: {
      name: theaterOrganizationReports,
    },
  },
]
